import { useContext, useEffect, useMemo, useState } from "react";

import styles from "./Banner.module.css";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import ThemeContext from "~/contexts/ThemeContext";
import BannerContext from "~/contexts/BannerContext";
import {
  actionFromTemplateBanner,
  BannerAction,
  BannerActionType,
  BannerCreative,
  creativeFromTemplateBanner,
  resolveBannerCreative,
} from "~/entities/bannerEntities";
import { useMediaQuery } from "~/hooks/useMediaQuery";

type BannerProps = {
  onBannerAction: (action: BannerAction) => void;
};

/** A banner reduced to what this component renders. */
type RenderableBanner = {
  creative: BannerCreative;
  dots: { colour: string; activeColour: string };
  action: BannerAction;
};

const Banner: React.FC<BannerProps> = ({ onBannerAction }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const { template } = useContext(ThemeContext);
  const { banners: onlineBanners } = useContext(BannerContext);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const banners = useMemo<RenderableBanner[]>(() => {
    // Orgs that have not been migrated yet still carry their banners on the template
    if (onlineBanners.length === 0) {
      return template.banners.reduce<RenderableBanner[]>((acc, banner) => {
        const creative = creativeFromTemplateBanner(banner, isMobile);
        if (creative) {
          acc.push({
            creative,
            dots: banner.dots,
            action: actionFromTemplateBanner(banner),
          });
        }
        return acc;
      }, []);
    }

    return onlineBanners.reduce<RenderableBanner[]>((acc, banner) => {
      const creative = resolveBannerCreative(banner, isMobile);
      if (creative) {
        acc.push({
          creative,
          dots: banner.dots,
          action: banner.action,
        });
      }
      return acc;
    }, []);
  }, [isMobile, onlineBanners, template.banners]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    setCurrentSlide(0);
  }, [isMobile]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Styles
  const dotColour = banners[currentSlide]?.dots.colour;
  const dotActiveColour = banners[currentSlide]?.dots.activeColour;

  const bannerShow = banners.length > 0;

  return (
    bannerShow && (
      <div className={styles.wrapper}>
        <div
          className={styles.slides}
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
          }}
        >
          {banners.map(({ creative, action }, index) => {
            // Banners stored before actions existed decode with an empty type
            const hasAction =
              !!action?.type && action.type !== BannerActionType.None;
            const backgroundImage = creative.backgroundImage;

            return (
              <div
                className={styles.slide}
                style={{
                  backgroundColor: creative.backgroundColour,
                  cursor: hasAction ? "pointer" : undefined,
                }}
                key={index}
                onClick={() => hasAction && onBannerAction(action)}
              >
                {backgroundImage && (
                  <img
                    className={styles.backgroundImage}
                    src={uploadcareConfig.getUrl(
                      backgroundImage.fileId,
                      backgroundImage.modifiers,
                    )}
                    alt={backgroundImage.fileName}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className={styles.dots}>
          {banners.map((_, index) => (
            <div
              key={index}
              className={styles.dot}
              style={{
                backgroundColor:
                  index === currentSlide ? dotActiveColour : dotColour,
              }}
              onClick={() => goToSlide(index)}
            ></div>
          ))}
        </div>
      </div>
    )
  );
};

export default Banner;
